import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { NotificationService } from 'src/app/Service/notification.service';
import { OtherService } from 'src/app/Service/other.service';
import { FraudAnalysisStatus, FraudCaseRow, FraudMovementCase, FraudRequest } from '../../fraud-dashboard.models';
import { buildCaseRows, FraudSessionExpiredError, getAnalysisProgress, parseFraudRows } from '../../fraud-dashboard.utils';
import { FraudSummary, FraudSummaryCard } from './fraud-overview.models';
import { buildSummaryCards } from './fraud-overview.utils';
import momentJalaali from 'moment-jalaali';
import axios from 'src/app/Service/axios';

@Component({
  selector: 'app-fraud-overview',
  templateUrl: './fraud-overview.component.html',
  styleUrls: ['./fraud-overview.component.css'],
})

export class FraudOverviewComponent implements OnInit, OnDestroy {
  readonly dateForm = new FormGroup({
    date: new FormControl(momentJalaali().subtract(1, 'day').format('jYYYY/jMM/jDD'))
  });
  summary: FraudSummary | null = null;
  summaryCards: FraudSummaryCard[] = [];
  cases: FraudCaseRow[] = [];
  analysis: FraudAnalysisStatus | null = null;
  summaryLoading = false;
  casesLoading = false;
  analysisLoading = false;
  summaryError = '';
  casesError = '';
  analysisError = '';

  private requestController?: AbortController;
  private requestId = 0;

  constructor(
    private gurdService: GurdServiceService,
    private other: OtherService,
    private notification: NotificationService,
    private router: Router
  ) {}

  get isLoading(): boolean {
    return this.summaryLoading || this.casesLoading || this.analysisLoading;
  }

  get analysisProgress(): number {
    return getAnalysisProgress(this.analysis);
  }

  ngOnInit(): void {
    void this.loadOverview();
  }

  ngOnDestroy(): void {
    this.requestId++;
    this.requestController?.abort();
  }

  async loadOverview(): Promise<void> {
    const requestId = ++this.requestId;
    this.requestController?.abort();
    const controller = new AbortController();
    this.requestController = controller;
    this.summary = null;
    this.summaryCards = [];
    this.cases = [];
    this.analysis = null;
    this.summaryError = this.casesError = this.analysisError = '';
    this.summaryLoading = this.casesLoading = this.analysisLoading = true;

    try {
      const user = Number(this.gurdService.decrypt(localStorage.getItem('ID')));
      const session = this.gurdService.decrypt(localStorage.getItem('Session'));
      if (!Number.isInteger(user) || user <= 0 || !session) throw new Error('Missing session');
      const payload: FraudRequest = {
        iSystemUSer: user,
        strSession: session,
        strDate: this.dateForm.value.date
      };
      await Promise.all([
        this.loadSummary(payload, requestId, controller.signal),
        this.loadCases(payload, requestId, controller.signal),
        this.loadAnalysis(payload, requestId, controller.signal)
      ]);
    } catch {
      if (requestId !== this.requestId) return;
      this.summaryError = this.casesError = this.analysisError = 'اطلاعات نشست معتبر نیست؛ لطفاً دوباره وارد سامانه شوید.';
      this.summaryLoading = this.casesLoading = this.analysisLoading = false;
    }
  }

  private async loadSummary(payload: FraudRequest, requestId: number, signal: AbortSignal): Promise<void> {
    try {
      const response = await axios.post('Fraud_Dashboard_GetSummary', payload, { timeout: 30000, signal });
      const rows = parseFraudRows<FraudSummary>(response.data);
      if (requestId !== this.requestId) return;
      this.summary = rows[0] || null;
      this.summaryCards = this.summary ? buildSummaryCards(this.summary) : [];
    } catch (error) {
      if (error instanceof FraudSessionExpiredError) {
        this.handleSessionExpired(requestId);
        return;
      }
      if (requestId === this.requestId) this.summaryError = 'دریافت خلاصهٔ آمار با خطا مواجه شد. دوباره تلاش کنید.';
    } finally {
      if (requestId === this.requestId) this.summaryLoading = false;
    }
  }

  private async loadCases(payload: FraudRequest, requestId: number, signal: AbortSignal): Promise<void> {
    try {
      const response = await axios.post('Fraud_MovementGroup_GetList', {
        ...payload,
        siReviewStatus: null,
        iPageNumber: 1,
        iPageSize: 5
      }, { timeout: 30000, signal });
      const rows = parseFraudRows<FraudMovementCase>(response.data);
      if (requestId === this.requestId) this.cases = buildCaseRows(rows);
    } catch (error) {
      if (error instanceof FraudSessionExpiredError) {
        this.handleSessionExpired(requestId);
        return;
      }
      if (requestId === this.requestId) this.casesError = 'دریافت پرونده‌های روز با خطا مواجه شد. دوباره تلاش کنید.';
    } finally {
      if (requestId === this.requestId) this.casesLoading = false;
    }
  }

  private async loadAnalysis(payload: FraudRequest, requestId: number, signal: AbortSignal): Promise<void> {
    try {
      const response = await axios.post('Fraud_Dashboard_GetAnalysisStatus', payload, { timeout: 30000, signal });
      const rows = parseFraudRows<FraudAnalysisStatus>(response.data);
      if (requestId === this.requestId) this.analysis = rows[0] || null;
    } catch (error) {
      if (error instanceof FraudSessionExpiredError) {
        this.handleSessionExpired(requestId);
        return;
      }
      if (requestId === this.requestId) this.analysisError = 'وضعیت پردازش در دسترس نیست. لطفاً دوباره تلاش کنید.';
    } finally {
      if (requestId === this.requestId) this.analysisLoading = false;
    }
  }

  private handleSessionExpired(requestId: number): void {
    if (requestId !== this.requestId) return;
    this.requestId++;
    this.requestController?.abort();
    this.summaryLoading = this.casesLoading = this.analysisLoading = false;
    this.notification.showError('لطفا وارد سیستم شوید');
    this.other.removeLocalStorage();
    void this.router.navigate(['login'], {
      queryParams: { error: 'لطفا وارد سیستم شوید' }
    });
  }
}
